import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';

import { submitSessionEnd } from '@ayeldo/frontend-core/auth/idpProxy';
import { isSafeRedirectUrl } from '@ayeldo/frontend-core/auth/redirectGuard';
import { resolveAuthAllowedRedirectHosts } from '@ayeldo/frontend-core/auth/resolveAuthAllowedRedirectHosts';

// Resolve the allow-list once at module load time so it is stable across renders.
const allowedHosts = resolveAuthAllowedRedirectHosts(import.meta.env);

const DEFAULT_LOCAL_IDP_URL = 'http://localhost:3010';

function resolveIdpApiBaseUrl(): string {
  const configured: unknown = import.meta.env.VITE_API_URL;
  const trimmed = typeof configured === 'string' ? configured.trim() : '';
  if (trimmed !== '') return trimmed;
  if (import.meta.env.DEV) return DEFAULT_LOCAL_IDP_URL;
  throw new Error('VITE_API_URL is not configured for production build');
}

/**
 * SessionEndPage — React proxy route for /session/end.
 *
 * The studio redirects here to end the IDP session.
 * This page POSTs to the IDP API and navigates to the returned redirectUrl.
 */
export function SessionEndPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const doSessionEnd = async () => {
      try {
        const idpApiBase = resolveIdpApiBaseUrl();
        const idTokenHint = searchParams.get('id_token_hint');
        const postLogoutRedirectUri = searchParams.get('post_logout_redirect_uri');
        const state = searchParams.get('state');
        const result = await submitSessionEnd(
          {
            ...(idTokenHint !== null ? { id_token_hint: idTokenHint } : {}),
            ...(postLogoutRedirectUri !== null
              ? { post_logout_redirect_uri: postLogoutRedirectUri }
              : {}),
            ...(state !== null ? { state } : {}),
          },
          { idpApiBase }
        );
        if (!cancelled) {
          if (!isSafeRedirectUrl(result.redirectUrl, allowedHosts)) {
            // eslint-disable-next-line no-console
            console.warn('SessionEndPage: rejected redirect target', result.redirectUrl);
            setStatus('error');
            setErrorMessage('Redirect blocked for safety. Please try again or contact support.');
            return;
          }
          window.location.href = result.redirectUrl;
        }
      } catch (err) {
        if (!cancelled) {
          setStatus('error');
          setErrorMessage(err instanceof Error ? err.message : 'Failed to complete sign-out.');
        }
      }
    };

    void doSessionEnd();

    return () => {
      cancelled = true;
    };
    // EXCEPTION: searchParams is a stable hook value. The session-end call must run exactly
    // once on mount with the URL params captured at that moment; depending on searchParams
    // would risk re-running mid-flight if the URL is updated.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (status === 'loading') {
    return (
      <Box
        sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Signing you out...
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <Stack spacing={2} alignItems="center" sx={{ maxWidth: 400, textAlign: 'center' }}>
        <Typography variant="h5" component="h1">
          Sign-out Error
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {errorMessage ?? 'Failed to complete sign-out. Please try again.'}
        </Typography>
      </Stack>
    </Box>
  );
}
